import React, { useEffect, useRef, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";

import {
  Block,
  BlockDes,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  PreviewCard,
  ReactDataTable,
  Row,
  Col,
  LoadingComponent,
} from "../../../components/Component";
import {
  ResidentTableData,
  fetchLocationDetail,
  fetchResidentList,
} from "../Resident/ResidentData";
import { useNavigate } from "react-router-dom";
import {
  CURRENCY_CODE,
  PER_PAGE_ITEMS,
  ServerStatusType,
  numberFormatter,
} from "../../../utils";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment/moment";
import { setRecoSetting } from "../../../store/authSlice";
import DataCard from "../../../components/partials/datacard/DataCard";
import TranscationType, {
  TranscationTypes,
} from "../Transaction/TranscationType";
import PendingReco from "../Reco/PendingReco";
import { PermissionType } from "../../../components/dropdowns/PermissionDropdown";
import ExportButton from "../../../components/button/ExportButton";
import { EXPORT_TYPE } from "../../../utils/Utils";
import { openLinkInNewTab } from "../../../utils/Functions";
import {
  UserActionType,
  UserPermissionType,
  isAllowToAccess,
} from "../../../services/permission/permission";
import { fetchResidentExport } from "../Resident/ResidentData";

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sessionUser = useSelector((state) => state?.auth?.sessionUser);
  const userType = sessionUser?.userType;
  const location = useSelector((state) => state?.auth?.currentLocation);
  const recoSetting = useSelector((state) => state?.auth?.comRecoSettings);
  const allLocations = useSelector((state) => state?.auth?.allLocations);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [residentList, setResidentList] = useState();
  const [locationDetails, setLocationDetails] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingComponents, setLoadingComponents] = useState({
    residentList: false,
    locationDetails: false,
    pendingReco: false,
    transactions: false
  });
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const loadResidentListRef = useRef(() => {});

  useEffect(() => {
    if (recoSetting === true) {
      loadResidentListRef.current();
      dispatch(setRecoSetting(false));
    }
  }, [recoSetting, dispatch]);

  // useEffect(() => {
  //   if (location) {
  //     loadLocationDetail();
  //     loadResidentList();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [location]);

  useEffect(() => {
    if (allLocations || location) {
      setCurrentPage(1);
      // Reset loading states when location changes
      setLoadingComponents({
        residentList: false,
        locationDetails: false,
        pendingReco: false,
        transactions: false
      });
      // Load data in sequence to avoid race conditions
      loadLocationDetail();
      if (currentPage === 1) {
        loadResidentList();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLocations, location]);

  useEffect(() => {
    if ((allLocations || location) && isInitialLoadComplete) {
      loadResidentList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Helper functions for loading state management
  const updateLoadingComponent = (component, loading) => {
    setLoadingComponents(prev => ({
      ...prev,
      [component]: loading
    }));
  };

  // Update overall loading when component loading states change
  useEffect(() => {
    const anyLoading = Object.values(loadingComponents).some(
      (loading) => loading
    );
    setIsLoading(anyLoading);
  }, [loadingComponents]);

  // API-------------------------------------------------------------->

  const loadResidentExport = (fType) => {
    let params = {
      locationId: allLocations ? undefined : (location ? location.id : null),
      search: null,
      status: ServerStatusType.active,
      date: moment().format("YYYY-MM-DD"),
      isLowBalance: true,
    };
    let fileType = fType;
    setIsExportLoading(true);
    fetchResidentExport(fileType, params, (data, error) => {
      setIsExportLoading(false);
      if (error === null) {
        openLinkInNewTab(data?.fileUrl);
      }
    });
  };

  const loadResidentList = () => {
    if (!allLocations && !location) return;
    updateLoadingComponent('residentList', true);
    let params = {
      pageInfo: {
        pageIndex: currentPage,
        itemsPerPage: PER_PAGE_ITEMS,
      },
      status: ServerStatusType.active,
      locationId: allLocations ? undefined : (location ? location.id : null),
      isLowBalance: true,
      date: moment().format("YYYY-MM-DD"),
    };
    fetchResidentList(params, (data, error) => {
      if (error === null) {
        setResidentList(data?.list);
        if (currentPage === 1) {
          setTotalItems(data?.pageInfo?.totalItems ?? 0);
        }
      }
      updateLoadingComponent('residentList', false);
      // Mark initial load as complete after both main API calls
      if (!loadingComponents.locationDetails) {
        setIsInitialLoadComplete(true);
      }
    });
  };

  loadResidentListRef.current = loadResidentList;

  const loadLocationDetail = () => {
    if (!allLocations && !location) return;
    updateLoadingComponent('locationDetails', true);
    let params = {
      locationId: allLocations ? undefined : (location?.id ?? sessionUser?.location?.id),
    };
    fetchLocationDetail(params, (data, error) => {
      if (error === null) {
        setLocationDetails(data);
      }
      updateLoadingComponent('locationDetails', false);
      // Mark initial load as complete after both main API calls
      if (!loadingComponents.residentList) {
        setIsInitialLoadComplete(true);
      }
    });
  };

  // Helper function to calculate aggregated balances
  const calculateAggregatedBalances = () => {
    if (!locationDetails) {
      return {
        cash: 0,
        bank: 0,
        totalResidentBalance: 0,
        totalActiveResidentBalance: 0,
        totalInactiveResidentBalance: 0,
        totalActiveBeds: 0,
        totalBeds: 0
      };
    }

    // Check if this is the "All Locations" response format
    if (allLocations && locationDetails.totalCashBalance !== undefined) {
      // All locations selected - use direct aggregated values from API
      return {
        cash: parseFloat(locationDetails.totalCashBalance) || 0,
        bank: parseFloat(locationDetails.totalBankBalance) || 0,
        totalResidentBalance: parseFloat(locationDetails.totalResidentBalance) || 0,
        totalActiveResidentBalance: parseFloat(locationDetails.totalActiveResidentBalance) || 0,
        totalInactiveResidentBalance: parseFloat(locationDetails.totalInactiveResidentBalance) || 0,
        totalActiveBeds: parseInt(locationDetails.totalActiveBeds) || 0,
        totalBeds: parseInt(locationDetails.totalBeds) || 0
      };
    }

    if (locationDetails.locations && Array.isArray(locationDetails.locations)) {
      // Multiple locations in array format - aggregate all balances
      let aggregatedCash = 0;
      let aggregatedBank = 0;
      let aggregatedTotalActiveBeds = 0;
      let aggregatedTotalBeds = 0;

      locationDetails.locations.forEach(location => {
        // Sum up cash and bank balances from all locations
        const locationCash = location?.accounts?.find(
          (item) => item?.type === TranscationTypes.cash
        )?.balance ?? 0;
        const locationBank = location?.accounts?.find(
          (item) => item?.type === TranscationTypes.bank
        )?.balance ?? 0;

        aggregatedCash += locationCash;
        aggregatedBank += locationBank;
        aggregatedTotalActiveBeds += location?.totalActiveBeds ?? 0;
        aggregatedTotalBeds += location?.totalBeds ?? 0;
      });

      return {
        cash: aggregatedCash,
        bank: aggregatedBank,
        totalResidentBalance: locationDetails?.totalResidentBalance ?? 0,
        totalActiveResidentBalance: locationDetails?.totalActiveResidentBalance ?? 0,
        totalInactiveResidentBalance: locationDetails?.totalInactiveResidentBalance ?? 0,
        totalActiveBeds: aggregatedTotalActiveBeds,
        totalBeds: aggregatedTotalBeds
      };
    } else {
      return {
        cash: locationDetails?.accounts?.find(
          (item) => item?.type === TranscationTypes.cash
        )?.balance ?? 0,
        bank: locationDetails?.accounts?.find(
          (item) => item?.type === TranscationTypes.bank
        )?.balance ?? 0,
        totalResidentBalance: locationDetails?.totalResidentBalance ?? 0,
        totalActiveResidentBalance: locationDetails?.totalActiveResidentBalance ?? 0,
        totalInactiveResidentBalance: locationDetails?.totalInactiveResidentBalance ?? 0,
        totalActiveBeds: locationDetails?.totalActiveBeds ?? 0,
        totalBeds: locationDetails?.totalBeds ?? 0
      };
    }
  };

  const balances = calculateAggregatedBalances();
  const { cash, bank, totalResidentBalance, totalActiveResidentBalance, totalInactiveResidentBalance, totalActiveBeds, totalBeds } = balances;

  return (
    <React.Fragment>
      <Head title="Dashboard" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Dashboard</BlockTitle>
              <BlockDes className="text-soft">
                <p>Welcome to {process.env.REACT_APP_APP_NAME}</p>
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Row className={"g-gs mb-3"}>
          <Col size={"12"} md={"4"} sm="6">
            <DataCard
              title={"Residents Balance"}
              amount={numberFormatter(CURRENCY_CODE, "" + totalResidentBalance)}
              backgroundcolor={"primary"}
            />
          </Col>
          <Col size={"12"} md={"4"} sm="6">
            <DataCard
              title={"Bank Balance"}
              amount={numberFormatter(CURRENCY_CODE, "" + bank)}
              backgroundcolor={"dark"}
            />
          </Col>
          <Col size={"12"} md={"4"} sm="6">
            <DataCard
              title={"Cash Balance"}
              amount={numberFormatter(CURRENCY_CODE, "" + cash)}
              backgroundcolor={"info"}
            />
          </Col>
          <Col size={"12"} md={"4"} sm="6">
            <DataCard
              title={"Active Resident Balance"}
              amount={numberFormatter(
                CURRENCY_CODE,
                "" + totalActiveResidentBalance
              )}
              backgroundcolor={"success"}
            />
          </Col>
          <Col size={"12"} md={"4"} sm="6">
            <DataCard
              title={"Inactive Resident Balance"}
              amount={numberFormatter(
                CURRENCY_CODE,
                "" + totalInactiveResidentBalance
              )}
              backgroundcolor={"danger"}
            />
          </Col>
          <Col size={"12"} md={"4"} sm="6">
            <DataCard
              title={"Beds Occupied"}
              amount={totalActiveBeds + "/" + totalBeds}
              backgroundcolor={"warning"}
            />
          </Col>
        </Row>

        {(location || allLocations) && isInitialLoadComplete && (
          <PendingReco
            locationId={allLocations ? undefined : location?.id}
            startDate={moment().subtract(1, "year").toDate()}
            endDate={new Date()}
            userType={userType}
            allLocations={allLocations}
            onLoadingChange={(loading) => updateLoadingComponent('pendingReco', loading)}
            showLoading={false}
          />
        )}

        <Block>
          <PreviewCard>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <p className="fs-5 mb-0">Low Balance Resident</p>
              {isAllowToAccess(
                UserPermissionType.resident,
                UserActionType.export
              ) && residentList?.length > 0 && (
                  <ExportButton
                    title={"Export"}
                    options={EXPORT_TYPE}
                    loading={isExportLoading}
                    onChange={(data) => {
                      loadResidentExport(data);
                    }}
                  />
                )}
            </div>
            <ReactDataTable
              key={`resident-table-${location?.id || 'all'}`}
              data={residentList}
              columns={ResidentTableData(true, null, allLocations)}
              expandableRows
              totalItemCount={totalItems}
              pagination
              currentPage={currentPage}
              onPageChanged={(page, count) => {
                setCurrentPage(page);
              }}
            />
          </PreviewCard>
        </Block>

        {!allLocations && (
          <Block className={"d-xxl-flex"}>
            <PreviewCard
              bodyClass={"mt-n4"}
              // className={"mt-2 w-xxl-50 w-100"}
              className={`mt-2 ${userType === PermissionType.admin ||
                userType === PermissionType.manager
                ? "w-xxl-50 w-100"
                : "w-100"
                }`}
            >
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <p className="fs-5 mb-0">Recent Transactions</p>
                <p
                  className="fs-7"
                  onClick={() => navigate("/transaction")}
                  style={{ cursor: "pointer" }}
                >
                  View All
                </p>
              </div>
              <div className="mt-n3">
                {(location || allLocations) && (
                  <TranscationType
                    locationId={allLocations ? undefined : location?.id}
                    startDate={moment().subtract(1, "year").toDate()}
                    endDate={new Date()}
                    type={TranscationTypes.dashboard}
                    showSpinner={false}
                  />
                )}
              </div>
            </PreviewCard>
          </Block>
        )}
      </Content>
      <LoadingComponent isLoading={isLoading} />
    </React.Fragment>
  );
};

export default Dashboard;
